import { useUserStore, config, helper, ErrorHandler } from '@/qingyu'
import axios from 'axios'

export default class BaseRequest {
  constructor() {
    this.config = {
      baseURL: config.env.baseURL,
      timeout: 30000,
    }
    
    // 创建axios实例
    this.axiosInstance = axios.create({
      baseURL: this.config.baseURL,
      timeout: this.config.timeout
    })
    
    // 请求拦截器
    this.axiosInstance.interceptors.request.use(
      config => {
        try {
          // 获取token
          const userStore = useUserStore()
          const token = userStore.token || ''
          
          // 不再添加token到请求头，改为在URL参数中添加
          // 在拦截器中不处理token，统一在request方法中处理
        } catch (error) {
          console.warn('获取用户token失败', error)
        }
        
        return config
      },
      error => {
        return Promise.reject(error)
      }
    )
    
    // 响应拦截器
    this.axiosInstance.interceptors.response.use(
      response => {
        return response
      },
      error => {
        return Promise.reject(error)
      }
    )
  }

  // 获取token的私有方法
  _getToken() {
    let token = ''
    try {
      // 首先尝试从Pinia存储获取
      const userStore = useUserStore()
      token = userStore.token || ''
      
      // 如果Pinia中没有token，尝试从localStorage直接获取
      if (!token) {
        const storedToken = localStorage.getItem('token')
        if (storedToken) {
          try {
            // 尝试解析JSON格式
            const parsedToken = JSON.parse(storedToken)
            token = parsedToken
          } catch (e) {
            // 如果不是JSON格式，直接使用字符串
            token = storedToken
          }
        }
      }
    } catch (error) {
      console.warn('获取用户token失败', error)
      // 尝试从localStorage直接获取
      try {
        const storedToken = localStorage.getItem('token')
        if (storedToken) {
          try {
            // 尝试解析JSON格式
            const parsedToken = JSON.parse(storedToken)
            token = parsedToken
          } catch (e) {
            // 如果不是JSON格式，直接使用字符串
            token = storedToken
          }
        }
      } catch (e) {
        console.error('从localStorage获取token失败', e)
      }
    }
    return token
  }

  async request(options) {
    const token = this._getToken()

    // 合并请求配置
    const requestConfig = {
      ...options,
      url: options.url,
      headers: {
        ...(options.method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
        ...options.header
      }
    }
    
    // 统一处理URL参数中的access-token
    if (token) {
      // 所有请求类型都通过URL查询参数传递access-token
      // 初始化params对象（如果不存在）
      requestConfig.params = requestConfig.params || {}
      
      // 添加access-token到URL查询参数
      requestConfig.params['access-token'] = token
    }

    // 显示loading
    if (options.loading) {
      // 创建loading元素
      this.showLoading(typeof options.loading === 'string' ? options.loading : '加载中')
    }

    try {
      // 发起请求
      const response = await this.axiosInstance(requestConfig)
      
      // 隐藏loading
      if (options.loading) {
        this.hideLoading()
      }
      
      // 处理响应数据
      if (response.data) {
        try {
          // 使用错误处理器处理响应
          const handler = ErrorHandler[response.data.code] || ErrorHandler.default
          const result = handler(response.data)
          return result
        } catch (error) {
          return Promise.reject(error)
        }
      } else {
        helper.tool.showToast({ title: '响应数据格式错误' })
        return Promise.reject(new Error('响应数据格式错误'))
      }
    } catch (error) {
      // 隐藏loading
      if (options.loading) {
        this.hideLoading()
      }
      
      // 超时错误
      if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
        helper.tool.showToast({ title: '请求超时' })
      } else {
        helper.tool.showToast({ title: '网络请求失败' })
      }
      
      return Promise.reject(error)
    }
  }

  // 显示loading
  showLoading(text) {
    // 创建loading元素
    if (!document.querySelector('#app-loading')) {
      const loading = document.createElement('div')
      loading.id = 'app-loading'
      loading.style.position = 'fixed'
      loading.style.top = '0'
      loading.style.left = '0'
      loading.style.width = '100%'
      loading.style.height = '100%'
      loading.style.display = 'flex'
      loading.style.justifyContent = 'center'
      loading.style.alignItems = 'center'
      loading.style.backgroundColor = 'rgba(0, 0, 0, 0.3)'
      loading.style.zIndex = '9999'
      
      const content = document.createElement('div')
      content.textContent = text
      content.style.padding = '15px 20px'
      content.style.backgroundColor = 'rgba(0, 0, 0, 0.7)'
      content.style.color = 'white'
      content.style.borderRadius = '5px'
      
      loading.appendChild(content)
      document.body.appendChild(loading)
    }
  }
  
  // 隐藏loading
  hideLoading() {
    const loading = document.querySelector('#app-loading')
    if (loading) {
      document.body.removeChild(loading)
    }
  }

  get(url, data, loading, header) {
    // 不再在这里添加token，统一在request方法中处理
    return this.request({
      url,
      method: 'GET',
      params: data, // Axios使用params而不是data来传递GET请求参数
      loading,
      header
    })
  }

  post(url, data, loading, header) {
    // 为所有POST请求添加group=pc参数，但不再在这里添加token
    let requestData = {
      ...data,
      group: 'pc'  // 添加固定的group参数
    }
    
    return this.request({
      url,
      method: 'POST',
      data: requestData,
      loading,
      header
    })
  }

  put(url, data, loading, header) {
    // 不再在这里添加token，统一在request方法中处理
    return this.request({
      url,
      method: 'PUT',
      data,
      loading,
      header
    })
  }

  delete(url, data, loading, header) {
    // 不再在这里添加token，统一在request方法中处理
    return this.request({
      url,
      method: 'DELETE',
      data,
      loading,
      header
    })
  }
} 